/**
 * CourseSidebar
 *
 * Handles course sidebar functionality:
 * - Tab switching (Innhold, Ressurser)
 * - Fullscreen toggle
 * - Close button
 * - Expandable text blocks
 */

import { Dom } from '@brandocms/jupiter'

export default class CourseSidebar {
  constructor(app, sidebarElement) {
    this.app = app
    this.sidebar = sidebarElement
    this.activeTab = 'innhold'
    this.init()
  }

  init() {
    this.setupTabHandlers()
    this.setupFullscreenHandler()
    this.setupCloseHandler()
    this.setupExpandableText()
    this.setupSmoothScrolling()
    this.setupAnchorScrollTracking()
    this.collapseOnMobile()
  }

  /**
   * Collapse tabs on mobile and ipad_portrait (screens below ipad_landscape)
   */
  collapseOnMobile() {
    const ipadLandscape = 1024
    if (window.innerWidth < ipadLandscape) {
      const tabTriggers = Dom.all('[data-tab-trigger]', this.sidebar)
      const tabContents = Dom.all('[data-tab-content]', this.sidebar)
      const toggleButton = this.sidebar.querySelector('[data-tab-toggle]')

      tabTriggers.forEach(t => {
        t.classList.remove('is-active')
        t.setAttribute('aria-pressed', 'false')
      })

      tabContents.forEach(content => {
        content.classList.remove('is-active')
      })

      if (toggleButton) {
        toggleButton.classList.add('is-collapsed')
        toggleButton.setAttribute('aria-label', 'Åpne')
      }

      this.activeTab = null
    }
  }

  /**
   * Smooth scrolling for anchor links
   */
  setupSmoothScrolling() {
    const anchorLinks = Dom.all('.anchor-link', this.sidebar)

    anchorLinks.forEach(link => {
      link.addEventListener('click', e => {
        e.preventDefault()

        const href = link.getAttribute('href')
        if (!href || !href.startsWith('#')) return

        const targetId = href.slice(1)
        const target = document.getElementById(targetId)

        if (!target) return

        // Get scroll-padding-top from html (base offset) + element's scroll-margin-top (extra offset)
        const htmlStyles = window.getComputedStyle(document.documentElement)
        const scrollPaddingTop = parseFloat(htmlStyles.scrollPaddingTop) || 0
        const targetStyles = window.getComputedStyle(target)
        const scrollMarginTop = parseFloat(targetStyles.scrollMarginTop) || 0
        const offset = scrollPaddingTop + scrollMarginTop

        // Calculate scroll position
        const elementPosition = target.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.pageYOffset - offset

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        })
      })
    })
  }

  /**
   * Track scroll position and update active anchor using IntersectionObserver
   */
  setupAnchorScrollTracking() {
    const anchorLinks = Dom.all('.anchor-link', this.sidebar)
    if (!anchorLinks.length) return

    // Build map of target elements to their anchor links
    const targetMap = new Map()
    anchorLinks.forEach(link => {
      const href = link.getAttribute('href')
      if (!href || !href.startsWith('#')) return

      const targetId = href.slice(1)
      const target = document.getElementById(targetId)
      if (target) {
        targetMap.set(target, link)
      }
    })

    if (!targetMap.size) return

    // Track which sections are currently visible
    const visibleSections = new Set()

    const updateAnchors = () => {
      // Find the topmost visible section
      const targets = Array.from(targetMap.keys())
      let activeTarget = null

      for (const target of targets) {
        if (visibleSections.has(target)) {
          activeTarget = target
          break
        }
      }

      // Update anchor classes
      anchorLinks.forEach(link => {
        link.closest('.nav-anchor')?.classList.remove('is-active', 'is-past')
      })

      if (activeTarget) {
        const activeLink = targetMap.get(activeTarget)
        const activeAnchor = activeLink?.closest('.nav-anchor')
        activeAnchor?.classList.add('is-active')

        // Mark anchors before active as "past"
        for (const target of targets) {
          if (target === activeTarget) break
          const link = targetMap.get(target)
          link?.closest('.nav-anchor')?.classList.add('is-past')
        }
      }
    }

    // Create observer with offset for navbar
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            visibleSections.add(entry.target)
          } else {
            visibleSections.delete(entry.target)
          }
        })
        updateAnchors()
      },
      {
        rootMargin: '-120px 0px -60% 0px',
      }
    )

    // Observe all target sections
    targetMap.forEach((link, target) => {
      observer.observe(target)
    })
  }

  /**
   * Tab switching functionality
   */
  setupTabHandlers() {
    const tabTriggers = Dom.all('[data-tab-trigger]', this.sidebar)
    const tabContents = Dom.all('[data-tab-content]', this.sidebar)

    tabTriggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const tabId = trigger.getAttribute('data-tab-trigger')

        // Update trigger states
        tabTriggers.forEach(t => {
          t.classList.remove('is-active')
          t.setAttribute('aria-pressed', 'false')
        })
        trigger.classList.add('is-active')
        trigger.setAttribute('aria-pressed', 'true')

        // Update content visibility
        tabContents.forEach(content => {
          content.classList.remove('is-active')
          if (content.getAttribute('data-tab-content') === tabId) {
            content.classList.add('is-active')
          }
        })

        this.activeTab = tabId
      })
    })
  }

  /**
   * Fullscreen toggle functionality
   */
  setupFullscreenHandler() {
    const fullscreenTrigger = this.sidebar.querySelector('[data-fullscreen-trigger]')

    if (!fullscreenTrigger) return

    fullscreenTrigger.addEventListener('click', () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(err => {
          console.warn('Error entering fullscreen:', err)
        })
      } else {
        document.exitFullscreen()
      }
    })

    // Update button state when fullscreen changes
    document.addEventListener('fullscreenchange', () => {
      if (document.fullscreenElement) {
        fullscreenTrigger.classList.add('is-active')
      } else {
        fullscreenTrigger.classList.remove('is-active')
      }
    })
  }

  /**
   * Toggle button - closes/opens tab content
   */
  setupCloseHandler() {
    const toggleButton = this.sidebar.querySelector('[data-tab-toggle]')

    if (!toggleButton) return

    toggleButton.addEventListener('click', () => {
      const tabTriggers = Dom.all('[data-tab-trigger]', this.sidebar)
      const tabContents = Dom.all('[data-tab-content]', this.sidebar)

      // Check if any tab is currently active
      const hasActiveTab = this.activeTab !== null

      if (hasActiveTab) {
        // Close: Remove active state from all triggers and contents
        tabTriggers.forEach(t => {
          t.classList.remove('is-active')
          t.setAttribute('aria-pressed', 'false')
        })

        tabContents.forEach(content => {
          content.classList.remove('is-active')
        })

        this.activeTab = null
        toggleButton.classList.add('is-collapsed')
        toggleButton.setAttribute('aria-label', 'Åpne')
      } else {
        // Open: Activate the first tab
        const firstTrigger = tabTriggers[0]
        const firstTabId = firstTrigger?.getAttribute('data-tab-trigger')

        if (firstTrigger && firstTabId) {
          firstTrigger.classList.add('is-active')
          firstTrigger.setAttribute('aria-pressed', 'true')

          tabContents.forEach(content => {
            if (content.getAttribute('data-tab-content') === firstTabId) {
              content.classList.add('is-active')
            }
          })

          this.activeTab = firstTabId
          toggleButton.classList.remove('is-collapsed')
          toggleButton.setAttribute('aria-label', 'Lukk')
        }
      }
    })
  }

  /**
   * Expandable text blocks
   */
  setupExpandableText() {
    const expandTriggers = Dom.all('[data-expandable-trigger]')

    expandTriggers.forEach(trigger => {
      // Skip if already initialized
      if (trigger.hasAttribute('data-expandable-initialized')) return
      trigger.setAttribute('data-expandable-initialized', '')

      trigger.addEventListener('click', () => {
        const container = trigger.closest('[b-tpl="expandableText"]')
        if (!container) return

        const content = container.querySelector('[data-expandable-content]')
        if (!content) return

        const isExpanded = trigger.getAttribute('aria-expanded') === 'true'

        if (isExpanded) {
          // Collapse
          content.hidden = true
          trigger.setAttribute('aria-expanded', 'false')
        } else {
          // Expand
          content.hidden = false
          trigger.setAttribute('aria-expanded', 'true')
        }
      })
    })
  }
}
